/**
 * User Statistics Cards
 *
 * Collection of card components for displaying user statistics on the admin dashboard.
 */

import type {
  GroupStats,
  ActiveUserStats,
  WatchlistStats,
  SessionStats,
  TopCompanyStats,
  TopQuestion,
} from '../../types';

// =============================================================================
// Shared Components
// =============================================================================

function LoadingCard({ rows = 1 }: { rows?: number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
      <div className="h-8 bg-gray-200 rounded w-24" />
      {rows > 1 && (
        <div className="mt-3 space-y-2">
          {Array.from({ length: rows - 1 }).map((_, i) => (
            <div key={`loading-row-${i}`} className="h-3 bg-gray-200 rounded w-full" />
          ))}
        </div>
      )}
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4">
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// =============================================================================
// Group Statistics Card
// =============================================================================

interface GroupStatsCardProps {
  data: GroupStats[] | undefined;
  totalGroups: number;
  avgMembers: number;
  isLoading: boolean;
  error: Error | null;
}

export function GroupStatsCard({
  data,
  totalGroups,
  avgMembers,
  isLoading,
  error,
}: GroupStatsCardProps) {
  if (error) {
    return <ErrorCard message={`Failed to load group stats: ${error.message}`} />;
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Groups</h3>
        <div className="grid grid-cols-2 gap-4">
          <LoadingCard />
          <LoadingCard />
        </div>
        <LoadingCard rows={3} />
      </div>
    );
  }

  // Sort groups by member count
  const sortedGroups = [...data].sort((a, b) => b.member_count - a.member_count);
  const topGroups = sortedGroups.slice(0, 5);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Groups</h3>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-3 bg-purple-50 rounded-lg text-center">
          <p className="text-xs text-purple-600 uppercase tracking-wide font-medium">
            Total Groups
          </p>
          <p className="text-2xl font-bold text-purple-700">{totalGroups}</p>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg text-center">
          <p className="text-xs text-purple-600 uppercase tracking-wide font-medium">
            Avg Members
          </p>
          <p className="text-2xl font-bold text-purple-700">{avgMembers.toFixed(1)}</p>
        </div>
      </div>

      {/* Top groups list */}
      {topGroups.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Largest Groups
          </p>
          {topGroups.map((group) => (
            <div
              key={group.group_id}
              className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <span className="text-sm text-gray-900 truncate flex-1 mr-2">
                {group.group_name}
              </span>
              <span className="text-sm font-medium text-gray-600">
                {group.member_count} members
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">No groups yet</p>
      )}
    </div>
  );
}

// =============================================================================
// Active Users Card
// =============================================================================

interface ActiveUsersCardProps {
  data: ActiveUserStats[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function ActiveUsersCard({ data, isLoading, error }: ActiveUsersCardProps) {
  if (error) {
    return <ErrorCard message={`Failed to load active users: ${error.message}`} />;
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Active Users</h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-32 mb-1" />
                <div className="h-3 bg-gray-200 rounded w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Active Users</h3>

      {data.length > 0 ? (
        <div className="space-y-3">
          {data.slice(0, 10).map((user, index) => (
            <div
              key={user.user_id}
              className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0"
            >
              {/* Rank badge */}
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  index === 0
                    ? 'bg-yellow-100 text-yellow-700'
                    : index === 1
                    ? 'bg-gray-200 text-gray-700'
                    : index === 2
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {index + 1}
              </div>

              {/* User info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.user_email || user.user_name || user.user_id}
                </p>
                <p className="text-xs text-gray-500">
                  Last active: {formatTimeAgo(user.last_active)}
                </p>
              </div>

              {/* Activity count */}
              <div className="text-right">
                <p className="text-sm font-semibold text-primary-600">
                  {formatNumber(user.activity_count)}
                </p>
                <p className="text-xs text-gray-400">events</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">No activity recorded</p>
      )}
    </div>
  );
}

// =============================================================================
// Watchlist Statistics Card
// =============================================================================

interface WatchlistStatsCardProps {
  data: WatchlistStats | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function WatchlistStatsCard({ data, isLoading, error }: WatchlistStatsCardProps) {
  if (error) {
    return <ErrorCard message={`Failed to load watchlist stats: ${error.message}`} />;
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Watchlist Stats</h3>
        <div className="grid grid-cols-2 gap-4 animate-pulse">
          <LoadingCard />
          <LoadingCard />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Watchlist Stats</h3>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-3 bg-teal-50 rounded-lg text-center">
          <p className="text-xs text-teal-600 uppercase tracking-wide font-medium">
            Users with Watchlist
          </p>
          <p className="text-2xl font-bold text-teal-700">
            {data.total_users_with_watchlist}
          </p>
        </div>
        <div className="p-3 bg-teal-50 rounded-lg text-center">
          <p className="text-xs text-teal-600 uppercase tracking-wide font-medium">
            Avg Companies
          </p>
          <p className="text-2xl font-bold text-teal-700">
            {data.avg_companies_per_watchlist.toFixed(1)}
          </p>
        </div>
      </div>

      {/* Total subscriptions */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
          Total Subscriptions
        </p>
        <p className="text-lg font-semibold text-gray-900">
          {formatNumber(data.total_subscriptions)}
        </p>
      </div>

      {/* Most watched companies */}
      {data.most_watched_companies.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Most Watched
          </p>
          {data.most_watched_companies.slice(0, 5).map((company) => (
            <div
              key={company.company_id}
              className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <span className="text-sm text-gray-900 truncate flex-1 mr-2">
                {company.company_name}
              </span>
              <span className="text-sm font-medium text-gray-600">
                {company.unique_users} users
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Session Statistics Card
// =============================================================================

interface SessionStatsCardProps {
  data: SessionStats | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function SessionStatsCard({ data, isLoading, error }: SessionStatsCardProps) {
  if (error) {
    return <ErrorCard message={`Failed to load session stats: ${error.message}`} />;
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Stats</h3>
        <div className="grid grid-cols-2 gap-4 animate-pulse">
          <LoadingCard />
          <LoadingCard />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Stats</h3>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 bg-blue-50 rounded-lg text-center">
          <p className="text-xs text-blue-600 uppercase tracking-wide font-medium">
            Today
          </p>
          <p className="text-2xl font-bold text-blue-700">
            {data.total_sessions_today}
          </p>
          <p className="text-xs text-blue-500">sessions</p>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg text-center">
          <p className="text-xs text-blue-600 uppercase tracking-wide font-medium">
            This Week
          </p>
          <p className="text-2xl font-bold text-blue-700">
            {data.total_sessions_week}
          </p>
          <p className="text-xs text-blue-500">sessions</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="p-3 bg-gray-50 rounded-lg text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Avg Duration
          </p>
          <p className="text-lg font-semibold text-gray-900">
            {formatDuration(data.avg_session_duration_seconds)}
          </p>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            Avg Page Views
          </p>
          <p className="text-lg font-semibold text-gray-900">
            {data.avg_page_views_per_session.toFixed(1)}
          </p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Top Companies Card
// =============================================================================

interface TopCompaniesCardProps {
  data: TopCompanyStats[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function TopCompaniesCard({ data, isLoading, error }: TopCompaniesCardProps) {
  if (error) {
    return <ErrorCard message={`Failed to load top companies: ${error.message}`} />;
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Companies</h3>
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Top Viewed Companies
      </h3>

      {data.length > 0 ? (
        <div className="space-y-2">
          {data.slice(0, 10).map((company, index) => (
            <div
              key={company.company_id}
              className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0"
            >
              {/* Rank */}
              <span className="text-sm font-medium text-gray-400 w-5">
                {index + 1}.
              </span>

              {/* Company name */}
              <span className="text-sm text-gray-900 truncate flex-1">
                {company.company_name}
              </span>

              {/* Stats */}
              <div className="text-right">
                <p className="text-sm font-semibold text-primary-600">
                  {formatNumber(company.view_count)}
                </p>
                <p className="text-xs text-gray-400">
                  {company.unique_users} users
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">
          No company views recorded
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Top Questions Card
// =============================================================================

interface TopQuestionsCardProps {
  data: TopQuestion[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function TopQuestionsCard({ data, isLoading, error }: TopQuestionsCardProps) {
  if (error) {
    return <ErrorCard message={`Failed to load top questions: ${error.message}`} />;
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Popular Chat Questions
        </h3>
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Popular Chat Questions
      </h3>

      {data.length > 0 ? (
        <div className="space-y-3">
          {data.slice(0, 10).map((question) => (
            <div
              key={question.question_text}
              className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0"
            >
              {/* Count badge */}
              <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-6 text-xs font-medium bg-primary-100 text-primary-700 rounded">
                {question.count}x
              </span>

              {/* Question text */}
              <p className="text-sm text-gray-700 line-clamp-2">
                {question.question_text}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">
          No chat questions recorded
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Chat Statistics Card
// =============================================================================

interface ChatStatsCardProps {
  totalConversations: number;
  totalMessages: number;
  avgMessagesPerConversation: number;
  isLoading: boolean;
}

export function ChatStatsCard({
  totalConversations,
  totalMessages,
  avgMessagesPerConversation,
  isLoading,
}: ChatStatsCardProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Chat Stats</h3>
        <div className="grid grid-cols-3 gap-4 animate-pulse">
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Chat Stats</h3>

      <div className="grid grid-cols-3 gap-4">
        <div className="p-3 bg-violet-50 rounded-lg text-center">
          <p className="text-xs text-violet-600 uppercase tracking-wide font-medium">
            Conversations
          </p>
          <p className="text-2xl font-bold text-violet-700">
            {formatNumber(totalConversations)}
          </p>
        </div>
        <div className="p-3 bg-violet-50 rounded-lg text-center">
          <p className="text-xs text-violet-600 uppercase tracking-wide font-medium">
            Messages
          </p>
          <p className="text-2xl font-bold text-violet-700">
            {formatNumber(totalMessages)}
          </p>
        </div>
        <div className="p-3 bg-violet-50 rounded-lg text-center">
          <p className="text-xs text-violet-600 uppercase tracking-wide font-medium">
            Avg/Conv
          </p>
          <p className="text-2xl font-bold text-violet-700">
            {avgMessagesPerConversation.toFixed(1)}
          </p>
        </div>
      </div>
    </div>
  );
}
